// src/contexts/PayrollContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import payrollService from '../services/payroll.service';

const PayrollContext = createContext();

export const usePayroll = () => {
    const context = useContext(PayrollContext);
    if (!context) {
        throw new Error('usePayroll must be used within a PayrollProvider');
    }
    return context;
};

// Local storage key for offline access
const PAYROLL_STORAGE_KEY = 'hrportal_payroll';

export const PayrollProvider = ({ children }) => {
    // Structure: { employeeId: { salaryStructure: [], paySlips: [], taxData: [], etc. } }
    const [payrollData, setPayrollData] = useState({});
    const [loading, setLoading] = useState(false);

    // Load payroll data from localStorage on component mount (for offline access)
    useEffect(() => {
        try {
            const stored = localStorage.getItem(PAYROLL_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                setPayrollData(parsed);
            }
        } catch (error) {
            console.error('Error loading payroll from localStorage:', error);
        }
    }, []);

    // Fetch payroll data from backend for current user
    const fetchPayrollData = async (userId) => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            //console.log('⚠️ No auth token, using localStorage payroll');
            return;
        }

        try {
            setLoading(true);

            // Fetch payslips
            const payslipsData = await payrollService.getMyPayslips();

            // Fetch salary structure
            const salaryStructure = await payrollService.getSalaryStructure();

            // Fetch tax declaration
            const taxData = await payrollService.getTaxDeclaration();

            // Map backend data to frontend format
            const employeePayroll = {
                paySlips: payslipsData.payslips || [],
                currentPayslip: payslipsData.current || null,
                salaryStructure: salaryStructure ? [salaryStructure] : [],
                taxData: taxData ? [taxData] : [],
                ctcBreakdown: salaryStructure?.ctc_breakdown || null,
                offCyclePayments: [],
                loans: [],
                advances: [],
                perquisites: []
            };

            setPayrollData(prev => ({
                ...prev,
                [userId]: employeePayroll
            }));

            // Save to localStorage for offline access
            payrollService.saveToLocalStorage(userId, employeePayroll);
        } catch (error) {
            console.error('Error fetching payroll data, using localStorage:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get employee payroll data
    const getEmployeePayroll = (employeeId) => {
        return payrollData[employeeId] || {
            salaryStructure: [],
            paySlips: [],
            taxData: [],
            ctcBreakdown: null,
            offCyclePayments: [],
            loans: [],
            advances: [],
            perquisites: []
        };
    };

    // Update employee salary structure
    const updateSalaryStructure = (employeeId, salaryData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                salaryStructure: Array.isArray(salaryData) ? salaryData : [salaryData]
            }
        }));
    };

    // Add pay slip
    const addPaySlip = (employeeId, paySlipData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                paySlips: [...(prev[employeeId]?.paySlips || []), paySlipData]
            }
        }));
    };

    // Update CTC breakdown
    const updateCTCBreakdown = (employeeId, ctcData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                ctcBreakdown: ctcData
            }
        }));
    };

    // Add tax data
    const addTaxData = (employeeId, taxData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                taxData: [...(prev[employeeId]?.taxData || []), taxData]
            }
        }));
    };

    // Add off-cycle payment
    const addOffCyclePayment = (employeeId, paymentData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                offCyclePayments: [...(prev[employeeId]?.offCyclePayments || []), paymentData]
            }
        }));
    };

    // Add loan
    const addLoan = (employeeId, loanData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                loans: [...(prev[employeeId]?.loans || []), loanData]
            }
        }));
    };

    // Add advance/deduction
    const addAdvanceDeduction = (employeeId, advanceData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                advances: [...(prev[employeeId]?.advances || []), advanceData]
            }
        }));
    };

    // Add perquisite
    const addPerquisite = (employeeId, perquisiteData) => {
        setPayrollData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                perquisites: [...(prev[employeeId]?.perquisites || []), perquisiteData]
            }
        }));
    };

    // Get all employees with payroll data
    const getAllEmployeesWithPayroll = () => {
        return Object.keys(payrollData);
    };

    // Clear employee payroll data
    const clearEmployeePayroll = (employeeId) => {
        setPayrollData(prev => {
            const newData = { ...prev };
            delete newData[employeeId];
            return newData;
        });
    };

    // Clear all payroll data
    const clearAllPayroll = () => {
        setPayrollData({});
        try {
            localStorage.removeItem(PAYROLL_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing payroll from localStorage:', error);
        }
    };

    // Generate pay slip
    const generatePaySlip = (employeeId, period, basicSalary, allowances = {}, deductions = {}) => {
        const grossSalary = basicSalary + Object.values(allowances).reduce((sum, val) => sum + val, 0);
        const totalDeductions = Object.values(deductions).reduce((sum, val) => sum + val, 0);
        const netSalary = grossSalary - totalDeductions;

        const paySlip = {
            id: `PAY_${employeeId}_${Date.now()}`,
            period,
            basicSalary,
            allowances,
            deductions,
            grossSalary,
            netSalary,
            generatedDate: new Date().toISOString(),
            status: 'generated'
        };

        addPaySlip(employeeId, paySlip);
        return paySlip;
    };

    // Calculate CTC breakdown
    const calculateCTCBreakdown = (annualCTC) => {
        const monthlyCTC = annualCTC / 12;

        return {
            annual: {
                basic: Math.round(annualCTC * 0.4),
                hra: Math.round(annualCTC * 0.2),
                lta: Math.round(annualCTC * 0.04),
                mobileInternet: 24000,
                fuelAllowance: 30000,
                specialAllowance: Math.round(annualCTC * 0.32),
                performanceBonus: Math.round(annualCTC * 0.04)
            },
            monthly: {
                basic: Math.round(monthlyCTC * 0.4),
                hra: Math.round(monthlyCTC * 0.2),
                lta: Math.round(monthlyCTC * 0.04),
                mobileInternet: 2000,
                fuelAllowance: 2500,
                specialAllowance: Math.round(monthlyCTC * 0.32),
                performanceBonus: Math.round(monthlyCTC * 0.04)
            }
        };
    };

    const value = {
        payrollData,
        loading,
        fetchPayrollData,
        getEmployeePayroll,
        updateSalaryStructure,
        addPaySlip,
        updateCTCBreakdown,
        addTaxData,
        addOffCyclePayment,
        addLoan,
        addAdvanceDeduction,
        addPerquisite,
        getAllEmployeesWithPayroll,
        clearEmployeePayroll,
        clearAllPayroll,
        generatePaySlip,
        calculateCTCBreakdown
    };

    return (
        <PayrollContext.Provider value={value}>
            {children}
        </PayrollContext.Provider>
    );
};